/**
 * Logger utility for WaveScoop frontend
 * Provides structured logging with environment-based filtering
 * Only logs in development mode to keep production clean
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: any;
  timestamp: string;
  context?: string;
}

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development';
  private minLevel: LogLevel = LogLevel.DEBUG;

  private formatMessage(entry: LogEntry): string {
    const levelName = LogLevel[entry.level];
    const context = entry.context ? `[${entry.context}]` : '';
    return `${entry.timestamp} ${levelName}${context}: ${entry.message}`;
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    data?: any,
    context?: string
  ): LogEntry {
    return {
      level,
      message,
      data,
      timestamp: new Date().toISOString(),
      context,
    };
  }

  private shouldLog(level: LogLevel): boolean {
    return this.isDevelopment && level >= this.minLevel;
  }

  debug(message: string, data?: any, context?: string): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;
    
    const entry = this.createLogEntry(LogLevel.DEBUG, message, data, context);
    console.debug(this.formatMessage(entry), data || '');
  }

  info(message: string, data?: any, context?: string): void {
    if (!this.shouldLog(LogLevel.INFO)) return;
    
    const entry = this.createLogEntry(LogLevel.INFO, message, data, context);
    console.info(this.formatMessage(entry), data || '');
  }

  warn(message: string, data?: any, context?: string): void {
    if (!this.shouldLog(LogLevel.WARN)) return;
    
    const entry = this.createLogEntry(LogLevel.WARN, message, data, context);
    console.warn(this.formatMessage(entry), data || '');
  }

  error(message: string, error?: Error | any, context?: string): void {
    if (!this.shouldLog(LogLevel.ERROR)) return;
    
    const entry = this.createLogEntry(LogLevel.ERROR, message, error, context);
    console.error(this.formatMessage(entry), error || '');
  }

  // Convenience methods for common use cases
  apiRequest(method: string, url: string, data?: any): void {
    this.debug(`API ${method.toUpperCase()} ${url}`, data, 'API');
  }

  apiResponse(method: string, url: string, status: number, data?: any): void {
    const level = status >= 400 ? LogLevel.ERROR : LogLevel.DEBUG;
    const message = `API ${method.toUpperCase()} ${url} - ${status}`;
    
    if (level === LogLevel.ERROR) {
      this.error(message, data, 'API');
    } else {
      this.debug(message, data, 'API');
    }
  }

  componentMount(componentName: string): void {
    this.debug(`Component mounted: ${componentName}`, undefined, 'React');
  }

  componentUnmount(componentName: string): void {
    this.debug(`Component unmounted: ${componentName}`, undefined, 'React');
  }

  userAction(action: string, data?: any): void {
    this.info(`User action: ${action}`, data, 'User');
  }

  hookEffect(hookName: string, dependencies?: any[]): void {
    this.debug(`Hook effect triggered: ${hookName}`, dependencies, 'Hook');
  }

  // Set minimum log level (useful for filtering)
  setMinLevel(level: LogLevel): void {
    this.minLevel = level;
  }
}

// Export a singleton instance
export const logger = new Logger();

// Export convenience functions that match common console methods
export const log = {
  debug: (message: string, data?: any, context?: string) => 
    logger.debug(message, data, context),
  info: (message: string, data?: any, context?: string) => 
    logger.info(message, data, context),
  warn: (message: string, data?: any, context?: string) => 
    logger.warn(message, data, context),
  error: (message: string, error?: Error | any, context?: string) => 
    logger.error(message, error, context),
  
  // Convenience methods
  api: {
    request: (method: string, url: string, data?: any) => 
      logger.apiRequest(method, url, data),
    response: (method: string, url: string, status: number, data?: any) => 
      logger.apiResponse(method, url, status, data),
  },
  
  component: {
    mount: (name: string) => logger.componentMount(name),
    unmount: (name: string) => logger.componentUnmount(name),
  },
  
  user: {
    action: (action: string, data?: any) => logger.userAction(action, data),
  },
  
  hook: {
    effect: (name: string, deps?: any[]) => logger.hookEffect(name, deps),
  },
  
  // Shortcut for user actions
  action: (action: string, data?: any) => logger.userAction(action, data),
};

export default logger;